import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { IconExternalLink, IconSelector } from "@tabler/icons-react";
import type { FC } from "react";
import {
  Button as AriaButton,
  MenuTrigger,
  type Placement,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";

interface UserProfileMenuItemBase {
  key: string;
  label: string;
}

/** Menu entry that navigates to a route. */
export interface UserProfileNavigationItem extends UserProfileMenuItemBase {
  href: string;
  /** Opens the link in a new tab and marks it with an external-link icon. */
  opensInNewWindow?: boolean;
}

/** Menu entry that runs a handler instead of navigating. */
export interface UserProfileActionItem extends UserProfileMenuItemBase {
  onAction: () => void;
}

export type UserProfileMenuItem =
  | UserProfileNavigationItem
  | UserProfileActionItem;

export interface UserProfileWidgetProps {
  name: string;
  email: string;
  menuItems: readonly UserProfileMenuItem[];
  /** Where the menu opens relative to the trigger. Defaults to the sidebar-friendly "top start". */
  menuPlacement?: Placement;
}

export const UserProfileWidget: FC<UserProfileWidgetProps> = ({
  name,
  email,
  menuItems,
  menuPlacement = "top start",
}) => (
  <MenuTrigger>
    <AriaButton
      aria-label="Open user menu"
      className={twMerge(
        "flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left",
        "hover:bg-sidebar-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
      )}
    >
      <span className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="truncate text-sm font-semibold text-sidebar-foreground">
          {name}
        </span>
        <span className="truncate text-xs text-foreground">{email}</span>
      </span>
      <IconSelector
        aria-hidden
        className="size-4 shrink-0 text-sidebar-foreground"
      />
    </AriaButton>
    <SimpleMenuPopover placement={menuPlacement}>
      <SimpleMenu aria-label="User menu">
        {menuItems.map((item) =>
          "href" in item ? (
            <SimpleMenuItem
              key={item.key}
              href={item.href}
              target={item.opensInNewWindow ? "_blank" : undefined}
              rel={item.opensInNewWindow ? "noreferrer" : undefined}
              textValue={item.opensInNewWindow ? item.label : undefined}
            >
              {item.opensInNewWindow ? (
                <>
                  <span className="min-w-0 flex-1 truncate" title={item.label}>
                    {item.label}
                  </span>
                  <IconExternalLink
                    aria-hidden
                    className="size-4 shrink-0 text-muted-foreground"
                  />
                </>
              ) : (
                item.label
              )}
            </SimpleMenuItem>
          ) : (
            <SimpleMenuItem key={item.key} onAction={item.onAction}>
              {item.label}
            </SimpleMenuItem>
          ),
        )}
      </SimpleMenu>
    </SimpleMenuPopover>
  </MenuTrigger>
);
